import React, { useEffect, useReducer, useMemo } from "react";
import { lockedReducer } from "../../../utils/defaultReducer";

import {
    Box,
    Typography,
    Tabs,
    Tab,
} from "@mui/material";

import HistoryIcon from "@mui/icons-material/History";
import EventIcon from "@mui/icons-material/Event";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import api from "../../../api";
import HistoricComponent from "./historic_component";
import EventsComponent from "./events_component";
import HistoricOpportunities from "./historic_opportunities";
import Loading from "../../Loading";

import { getSession } from "../../../auth";

const TabsData = (
    contact,
    state = {},
    setState = () => {},
    supervisedQueues = []
) => ({
    historic: {
        name: "Histórico",
        label: "Histórico de contatos",
        icon: <HistoryIcon fontSize="small" />,
        component: (
            <HistoricComponent
                contact={contact}
                data={state.historic}
                setParent={setState}
                supervisedQueues={supervisedQueues}
                category={state.category}
            />
        ),
        permission: true,
        url: `/api/enterprises/${contact.enterprise}/contacts/cursor/${contact.id}/0/?queues=${supervisedQueues.join(",")}`,
    },

    events: {
        name: "Eventos",
        label: "Histórico de eventos",
        icon: <EventIcon fontSize="small" />,
        component: (
            <EventsComponent
                data={state.events}
                setParent={setState}
            />
        ),
        permission: getSession().profile.enterprise.show_agenda,
        url: `/api/enterprises/${contact.enterprise}/contacts/${contact.id}/tasks/`,
    },

    opportunities: {
        name: "Oportunidades",
        label: "Histórico de oportunidades",
        icon: <TrendingUpIcon fontSize="small" />,
        component: (
            <HistoricOpportunities
                contact={contact}
                data={state.opportunities}
                setParent={setState}
            />
        ),
        permission: getSession().profile.enterprise.show_agenda,
        url: `/api/enterprises/${contact.enterprise}/contacts/${contact.id}/opportunities/?cursor=0`,
    },
});

const initialize = async (
    contact,
    setState,
    supervisedQueues = []
) => {
    setState({ loading: true });

    const tabs = Object.entries(
        TabsData(contact, {}, () => {}, supervisedQueues)
    ).filter(([, value]) => value.permission);

    const responses = await Promise.all(
        tabs.map(([key, { url }]) =>
            api.get(url).then((result) => ({
                ...result,
                hint: key,
            }))
        )
    );

    let newState = {
        loading: false,
    };

    responses.forEach((response) => {
        if (response.status === 200) {
            newState = {
                ...newState,
                [response.hint]: response.data,
            };
        }
    });

    setState(newState);
};

const tabbedMenu = true;

const ProfileTab = ({
    contact,
    supervisedQueues = [],
}) => {
    const [state, setState] = useReducer(lockedReducer, {
        tab: "historic",
        historic: [[], 0],
        opportunities: [[], 0],
        events: [],
        view: null,
        loading: false,
        category: "ALL",
    });

    useEffect(() => {
        initialize(contact, setState, supervisedQueues);
    }, [contact.id]);

    const tabs = useMemo(
        () =>
            TabsData(
                contact,
                state,
                setState,
                supervisedQueues
            ),
        [
            state.historic,
            state.events,
            state.opportunities,
            state.category,
            supervisedQueues,
        ]
    );

    return (
        <>
            {tabbedMenu ? (
                <Box className="profiletab">
                    <Loading loading={state.loading} />

                    <Tabs
                        value={state.tab}
                        onChange={(_, value) => setState({ tab: value })}
                        variant="scrollable"
                        scrollButtons={false}
                        sx={{
                            minHeight: 40,
                            borderBottom: "1px solid",
                            borderColor: "divider",

                            "& .MuiTabs-flexContainer": {
                                justifyContent: "flex-start",
                                gap: 1,
                            },

                            "& .MuiTabs-indicator": {
                                height: 4,
                                borderRadius: "4px 4px 0 0",
                                backgroundColor: "primary.main",
                            },
                        }}
                    >
                        {Object.entries(tabs)
                            .filter(([, value]) => value.permission)
                            .map(([key, value]) => (
                                <Tab
                                    key={key}
                                    value={key}
                                    icon={value.icon}
                                    iconPosition="start"
                                    label={value.name}
                                    disableRipple
                                    sx={{
                                        minHeight: 40,
                                        minWidth: 0,
                                        px: 1.5,
                                        py: 0,
                                        textTransform: "none",
                                        fontSize: ".9rem",
                                        fontWeight: 500,
                                        color: "text.secondary",

                                        "& .MuiTab-iconWrapper": {
                                            mr: 0.75,
                                        },

                                        "&.Mui-selected": {
                                            color: "primary.main",
                                            fontWeight: 700,
                                        },
                                    }}
                                />
                            ))}
                    </Tabs>

                    <Box
                        className="profilecomponent"
                        sx={{
                            pt: 2,
                        }}
                    >
                        {tabs[state.tab]?.component}
                    </Box>
                </Box>
            ) : (
                <Box className="profiletab untabbed">
                    <Loading loading={state.loading} />

                    {Object.entries(tabs).map(
                        ([key, value]) => {
                            if (!value.permission) return null;

                            return (
                                <Box
                                    key={key}
                                    className="profilecomponent"
                                    sx={{
                                        flex:
                                            key === "historic"
                                                ? "2 1 0"
                                                : "1 1 0",
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        className="label"
                                    >
                                        {value.label}
                                    </Typography>

                                    {value.component}
                                </Box>
                            );
                        }
                    )}
                </Box>
            )}
        </>
    );
};

export default ProfileTab;